import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { every, chain } from 'underscore';
import classNames from 'classnames';
import { BidAdjustment } from '@bingads-webui-campaign/target-bid-adjustment';
import { AudienceTargetInfo } from '@bingads-webui-campaign/audience-target-info';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getTargetCriterion, RemovedDeviceBidAdjustment, AddedDeviceBidAdjustment } from '@bingads-webui-campaign-react/xandr-campaign';

import { AgeRanges, DeviceTypes, GenderTypes } from '../../../common/constants';
import { getBidAdjustmentStyles } from './styles/bid-adjustment-styles';

const typeToValidationKey = {
  Age: {
    allAvailableSelection: AgeRanges,
    initialSelection: 'initialAgeRanges',
    selection: 'ageRanges',
    selectionWithBidAdjustment: 'ageRangesWithBidAdjustment',
  },
  Gender: {
    allAvailableSelection: GenderTypes,
    initialSelection: 'initialGenderTypes',
    selection: 'genderTypes',
    selectionWithBidAdjustment: 'genderTypesWithBidAdjustment',
  },
  Device: {
    allAvailableSelection: DeviceTypes,
    initialSelection: 'initialDeviceTypes',
    selection: 'deviceTypes',
    selectionWithBidAdjustment: 'deviceTypesWithBidAdjustment',
  },
  DeviceCampaign: {
    allAvailableSelection: DeviceTypes,
    initialSelection: 'initialCampaignLevelDeviceTargets',
    selection: 'campaignLevelDeviceTargets',
    selectionWithBidAdjustment: 'campaignDeviceTypesWithBidAdjustment',
  },
};

const BidAdjustmentInternal = (props) => {
  const [bidAdjustmentRef, setBidAdjustmentRef] = useState();
  const [hasBidValueChanged, setHasBidValueChanged] = useState(false);

  const classes = useDefaultStyles(getBidAdjustmentStyles);

  const {
    data: { adGroup, campaign },
    updateValidation,
    criterionType,
    readOnly = false,
    criterionLevel = 'AdGroupCriterion',
    isAudienceEdit = false,
  } = props;

  const currentLevelTargetInfo = useMemo(() => (
    new AudienceTargetInfo({
      resourceName: criterionLevel,
      isForVNext: true,
      initAudienceAssociationView: false,
    })), [criterionLevel]);

  const view = useMemo(() => {
    const bidAdjustmentGridview = new BidAdjustment({
      currentLevelTargetInfo,
      isForVNext: true,
      criterionTypes: [criterionType],
      supportCashbackSettings: false,
      showExclusion: false,
      readonly: readOnly,
    });

    bidAdjustmentGridview.render();

    return bidAdjustmentGridview;
  }, [criterionType, currentLevelTargetInfo, readOnly]);

  const onChange = useCallback(() => {
    const updatedItems = currentLevelTargetInfo.getSnapshot(false);

    const targets = updatedItems.filter(({ CriterionType }) => (CriterionType === criterionType));

    let validationKey = typeToValidationKey[criterionType];

    if (criterionType === 'Device' && criterionLevel === 'CampaignCriterion') {
      validationKey = typeToValidationKey.DeviceCampaign;
    }

    updateValidation([], { [validationKey.selectionWithBidAdjustment]: targets });
  }, [criterionType, currentLevelTargetInfo, updateValidation, criterionLevel]);


  useEffect(() => {
    if (bidAdjustmentRef && bidAdjustmentRef.childElementCount === 0 && view) {
      bidAdjustmentRef.innerHTML = '';
      bidAdjustmentRef.append(view.el);
    }
  }, [bidAdjustmentRef, view]);

  useEffect(() => {
    if (criterionLevel && bidAdjustmentRef && view) {
      bidAdjustmentRef.innerHTML = '';

      view.render();
      bidAdjustmentRef.append(view.el);
    }
  }, [criterionLevel, bidAdjustmentRef, view]);

  useEffect(() => {
    if (bidAdjustmentRef) {
      currentLevelTargetInfo.off(`${criterionLevel}${criterionType}Changed`, () => onChange());

      let validationKey = typeToValidationKey[criterionType];

      if (criterionType === 'Device' && criterionLevel === 'CampaignCriterion') {
        validationKey = typeToValidationKey.DeviceCampaign;
      }

      const {
        [validationKey.selection]: selection,
        [validationKey.initialSelection]: initialSelection,
      } = adGroup || campaign;

      const negativeSelection = chain(validationKey.allAvailableSelection).values().difference(selection).value();
      const currentTargets = currentLevelTargetInfo.getSnapshot(false).filter(({ CriterionType }) => (CriterionType === criterionType));
      const currentNegativeTargets = currentLevelTargetInfo.getSnapshot(false).filter(({ CriterionType }) => (CriterionType === `Negative${criterionType}`));

      const targetKey = criterionType === 'Device' ? 'DeviceName' : 'Target';

      // check if the current targets are the same as the selected targets
      const isTargetSame = every(currentTargets, ((target) => {
        const { Criterion } = target;
        const { [`${criterionType}Criterion`]: TypeCriterion } = Criterion;

        return selection.includes(TypeCriterion[targetKey]);
      }));

      const isNegativeTargetSame = every(currentNegativeTargets, ((target) => {
        const { Criterion } = target;
        const { [`${criterionType}Criterion`]: TypeCriterion } = Criterion;

        return negativeSelection.includes(TypeCriterion[targetKey]);
      }));

      const updatedCriterion = [];

      if ((selection && (currentTargets.length !== selection.length || !isTargetSame)) ||
        (negativeSelection && (currentNegativeTargets.length !== negativeSelection.length || !isNegativeTargetSame))) {
        const initialSelectionBidAdjustment = initialSelection.reduce((accumulator, currentValue) => {
          const { criterion, bidAdjustment } = currentValue;
          const updatedValue = accumulator;
          updatedValue[criterion] = bidAdjustment;
          return updatedValue;
        }, {});

        const currentBidAdjustment = currentTargets.reduce((accumulator, currentValue) => {
          const { Criterion: criterion } = currentValue;
          const { BidAdjustment: bidAdjustment } = criterion;
          const key = criterion[`${criterionType}Criterion`][targetKey];
          const updatedValue = accumulator;
          updatedValue[key] = bidAdjustment;
          return updatedValue;
        }, {});

        const criterionList = selection.map((criterion) => {
          let finalizedBid = currentBidAdjustment[criterion] || initialSelectionBidAdjustment[criterion];
          if (criterionType === 'Device' && isAudienceEdit === true) {
            const criterionBid = currentBidAdjustment[criterion] || initialSelectionBidAdjustment[criterion];
            const bid = criterionBid === RemovedDeviceBidAdjustment && hasBidValueChanged === true ? AddedDeviceBidAdjustment : criterionBid;
            finalizedBid = bid === undefined ? AddedDeviceBidAdjustment : bid;
          }
          return getTargetCriterion({
            targets: [{ criterion }],
            type: criterionType,
            propKey: targetKey,
            adGroupViewModel: { data: adGroup },
            campaignViewModel: { data: campaign },
            level: criterionLevel === 'CampaignCriterion' ? 'campaign' : 'adGroup',
            bidAdjustment: finalizedBid,
          })[0];
        });

        updatedCriterion.push(...criterionList);

        const negativeCriterionList = negativeSelection
          .filter(negSelection => !selection.includes(negSelection.criterion))
          .map(criterion => getTargetCriterion({
            targets: [{ criterion }],
            type: `Negative${criterionType}`,
            overrideType: criterionType,
            propKey: targetKey,
            adGroupViewModel: { data: adGroup },
            level: criterionLevel === 'CampaignCriterion' ? 'campaign' : 'adGroup',
            campaignViewModel: { data: campaign },
          })[0]);

        updatedCriterion.push(...negativeCriterionList);
      }

      if (updatedCriterion.length > 0) {
        currentLevelTargetInfo.destroyAllAsync();
        currentLevelTargetInfo.createMultipleAsync(updatedCriterion);
      }

      currentLevelTargetInfo.on(`${criterionLevel}${criterionType}Changed`, () => onChange());
      setHasBidValueChanged(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign, adGroup, view, bidAdjustmentRef]);

  return (
    <div className={classNames(classes.pickerContainer)}>
      <div className="criterion-setting-container">
        <div className="target-edit-container">
          <div ref={newRef => setBidAdjustmentRef(newRef)} />
        </div>
      </div>
    </div>
  );
};

BidAdjustmentInternal.propTypes = {
  data: PropTypes.shape({
    adGroup: PropTypes.shape({
      ageRanges: PropTypes.arrayOf(PropTypes.string).isRequired,
      initialAgeRanges: PropTypes.arrayOf(PropTypes.shape({
        criterion: PropTypes.string.isRequired,
        bidAdjustment: PropTypes.number.isRequired,
      })).isRequired,
      deviceTargetingCampaignLevel: PropTypes.bool.isRequired,
    }).isRequired,
    campaign: PropTypes.shape({}).isRequired,
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  criterionType: PropTypes.string.isRequired,
  isAudienceEdit: PropTypes.bool,
};

export const ValidatedBidAdjustment = withCustomValidation(BidAdjustmentInternal);
